// MARK://dhk8/admin-shell/pages/modules/OrganizationDetailPage
// Detalle de una organización → dhk8-platform-tenants:
//   /v1/orgs/:id/rollup/sales        (V1 — existente)
//   /v1/orgs/:id/rollup/inventory    (Stream 6)
//   /v1/orgs/:id/rollup/customers    (Stream 6)
//   /v1/orgs/:id/rollup/products-top (Stream 6)
//   /v1/orgs/:id/rollup/kpis         (Stream 6)
//
// Vista:
//   - Header con displayName + orgSlug.
//   - Selector de período compartido (hoy / semana / mes / todo / custom desde/hasta).
//   - Tabs: Ventas · Inventario · Clientes · Productos top · KPIs.
//   - Cada tab pinta su rollup específico. Drill-down a branch usa el slug del tenant
//     (mismo patrón que `Account.name` en /accounts).
//
// ⚠️ Stream 6 — decisión codificada del fundador: NO se calcula dinero (regalías,
// comisiones, splits). Solo lecturas operativas. Esto está documentado en el
// componente `MoneyExclusionNote` y replicado en la doc del rollup service.
import type { JSX } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, RefreshCw, Building, AlertTriangle, Users, Package, Trophy, Activity } from 'lucide-react'
import * as orgs from '../../lib/clients/platform-orgs.js'
import { unitShort } from '../../components/UnitPicker.js'
import { useAsync, asyncState } from '../../lib/use-async.js'

function mxn(cents: number): string {
  return (cents / 100).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
}

function fmtNumber(n: number, fractionDigits = 0): string {
  return n.toLocaleString('es-MX', { maximumFractionDigits: fractionDigits })
}

type Preset = 'today' | 'week' | 'month' | 'all' | 'custom'
type Tab = 'sales' | 'inventory' | 'customers' | 'products' | 'kpis'

const TABS: { id: Tab; label: string; Icon: typeof Building }[] = [
  { id: 'sales',     label: 'Ventas',         Icon: Trophy },
  { id: 'inventory', label: 'Inventario',     Icon: Package },
  { id: 'customers', label: 'Clientes',       Icon: Users },
  { id: 'products',  label: 'Productos top',  Icon: Trophy },
  { id: 'kpis',      label: 'KPIs',           Icon: Activity },
]

const DOW_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

export default function OrganizationDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()

  const [preset, setPreset] = useState<Preset>('month')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [tab, setTab] = useState<Tab>('sales')
  const [topLimit, setTopLimit] = useState<number>(10)

  const period: { from?: string; to?: string } = preset === 'custom'
    ? {
        ...(customFrom ? { from: new Date(customFrom).toISOString() } : {}),
        ...(customTo ? { to: new Date(customTo).toISOString() } : {}),
      }
    : orgs.periodToRange(preset)

  // Sales — siempre lo cargamos para resolver displayName + branches (rememberOrg).
  const salesRollup = useAsync(
    (s) => orgs.getOrgRollup(id, period, s),
    [id, preset, customFrom, customTo],
  )

  useEffect(() => {
    if (salesRollup.data) {
      orgs.rememberOrg({
        id: salesRollup.data.orgId,
        displayName: salesRollup.data.orgName,
        slug: salesRollup.data.orgSlug,
        branchCount: salesRollup.data.branches.length,
      })
    }
  }, [salesRollup.data])

  const r = salesRollup.data

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/organizations')} aria-label="Volver">
            <ChevronLeft size={18} />
          </button>
          <div>
            <h1 className="adm-page__title">{r?.orgName ?? 'Organización'}</h1>
            <p className="adm-page__sub">
              {r ? <>Slug: <code>{r.orgSlug}</code> · {r.totals.branchCount} sucursal{r.totals.branchCount === 1 ? '' : 'es'}</> : id}
            </p>
          </div>
        </div>
        <div className="adm-page__actions">
          <button className="adm-btn" onClick={salesRollup.reload} disabled={salesRollup.loading} aria-label="Recargar">
            <RefreshCw size={15} /> Recargar
          </button>
        </div>
      </div>

      <div className="adm-card adm-card--pad" style={{ marginBottom: 16 }}>
        <div className="adm-card__title">Período</div>
        <div role="group" aria-label="Selector de período" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {(['today', 'week', 'month', 'all', 'custom'] as Preset[]).map((p) => (
            <button
              key={p}
              type="button"
              className={`adm-btn adm-btn--sm${preset === p ? ' adm-btn--primary' : ' adm-btn--ghost'}`}
              onClick={() => setPreset(p)}
              aria-pressed={preset === p}
            >
              {p === 'today' ? 'Hoy' : p === 'week' ? 'Última semana' : p === 'month' ? 'Este mes' : p === 'all' ? 'Todo' : 'Personalizado'}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <label className="adm-field">
              <span className="adm-field__label">Desde</span>
              <input
                type="datetime-local"
                className="adm-input"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </label>
            <label className="adm-field">
              <span className="adm-field__label">Hasta</span>
              <input
                type="datetime-local"
                className="adm-input"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </label>
          </div>
        )}
        {tab === 'inventory' && (
          <p className="adm-row__meta" style={{ marginTop: 8, fontStyle: 'italic' }}>
            Inventario es un snapshot — el período NO se aplica.
          </p>
        )}
      </div>

      <div className="adm-tabs" role="tablist" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`adm-btn adm-btn--sm${tab === t.id ? ' adm-btn--primary' : ' adm-btn--ghost'}`}
            onClick={() => setTab(t.id)}
          >
            <t.Icon size={14} style={{ verticalAlign: 'middle', marginRight: 6, opacity: 0.7 }} />
            {t.label}
          </button>
        ))}
      </div>

      <MoneyExclusionNote tab={tab} />

      {tab === 'sales'     && <SalesTab loading={salesRollup.loading} error={salesRollup.error} reload={salesRollup.reload} data={r ?? undefined} />}
      {tab === 'inventory' && <InventoryTab orgId={id} />}
      {tab === 'customers' && <CustomersTab orgId={id} period={period} periodKey={`${preset}/${customFrom}/${customTo}`} />}
      {tab === 'products'  && <ProductsTab orgId={id} period={period} periodKey={`${preset}/${customFrom}/${customTo}`} limit={topLimit} setLimit={setTopLimit} />}
      {tab === 'kpis'      && <KpisTab orgId={id} period={period} periodKey={`${preset}/${customFrom}/${customTo}`} />}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-componentes por tab
// ─────────────────────────────────────────────────────────────────────────

function MoneyExclusionNote({ tab }: { tab: Tab }): JSX.Element | null {
  // Solo mostramos el caveat en tabs donde el usuario podría esperar números
  // monetarios distribuidos (regalías/comisiones). Sales y KPIs muestran totales
  // ya cerrados — el caveat es para que quede claro que NO hay distribución.
  if (tab !== 'sales' && tab !== 'kpis') return null
  return (
    <div className="adm-callout adm-callout--info" style={{ marginBottom: 12, fontSize: 12 }}>
      Solo reportes operativos. No incluye cálculo de regalías, comisiones ni splits de franquicia
      (pendiente de decisión del fundador).
    </div>
  )
}

function SalesTab({
  loading, error, reload, data,
}: {
  loading: boolean
  error: unknown
  reload: () => void
  data: orgs.OrgRollup | undefined
}): JSX.Element {
  return (
    <>
      {asyncState(loading, error instanceof Error ? error.message : error ? String(error) : null, reload)}
      {data && (
        <>
          <div className="adm-grid" style={{ marginBottom: 16 }}>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Ventas totales</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{mxn(data.totals.salesCents)}</div>
              <div className="adm-row__meta" style={{ marginTop: 4 }}>
                {data.period.from || data.period.to ? `${data.period.from?.slice(0, 10) ?? '…'} → ${data.period.to?.slice(0, 10) ?? '…'}` : 'Histórico completo'}
              </div>
            </div>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Órdenes cerradas</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{data.totals.orderCount.toLocaleString('es-MX')}</div>
            </div>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Sucursales</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{data.totals.branchCount}</div>
            </div>
          </div>

          <BranchBarChart
            title="Ventas por sucursal"
            rows={data.branches.map((b) => ({ tenantId: b.tenantId, brand: b.brand, label: mxn(b.salesCents), value: b.salesCents }))}
          />

          <div className="adm-card adm-card--pad0">
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--pos-separator)' }}>
              <h2 className="adm-card__title" style={{ margin: 0 }}>Sucursales (branches)</h2>
            </div>
            <div className="adm-list">
              {data.branches.length === 0 && (
                <div className="adm-empty">Esta organización aún no tiene sucursales asociadas.</div>
              )}
              {data.branches.map((b) => (
                <div key={b.tenantId} className="adm-row">
                  <div className="adm-row__main">
                    <BranchTitle brand={b.brand} />
                    <div className="adm-row__meta">
                      {b.displayName}
                      <br />
                      <span style={{ fontSize: 11 }}>tenant: {b.tenantId}</span>
                    </div>
                  </div>
                  <div className="adm-row__tail" style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600 }}>{mxn(b.salesCents)}</div>
                    <div className="adm-row__meta">{b.orderCount.toLocaleString('es-MX')} órd.</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  )
}

function InventoryTab({ orgId }: { orgId: string }): JSX.Element {
  const rollup = useAsync((s) => orgs.getOrgInventoryRollup(orgId, s), [orgId])
  const d = rollup.data
  return (
    <>
      {asyncState(rollup.loading, rollup.error, rollup.reload)}
      {d && (
        <>
          <div className="adm-grid" style={{ marginBottom: 16 }}>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Total de insumos</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{fmtNumber(d.totals.stockItemCount)}</div>
            </div>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Cantidad total</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{fmtNumber(d.totals.totalQty, 2)}</div>
              <div className="adm-row__meta">unidades mixtas</div>
            </div>
            <div className="adm-card adm-card--pad" style={{ background: d.totals.lowStockCount > 0 ? 'var(--pos-warning-bg)' : undefined }}>
              <div className="adm-card__title">Bajo stock</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{fmtNumber(d.totals.lowStockCount)}</div>
              <div className="adm-row__meta">qty ≤ reorder</div>
            </div>
            <div className="adm-card adm-card--pad" style={{ background: d.totals.outOfStockCount > 0 ? 'var(--pos-danger-bg)' : undefined }}>
              <div className="adm-card__title">Agotados</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{fmtNumber(d.totals.outOfStockCount)}</div>
              <div className="adm-row__meta">qty ≤ 0</div>
            </div>
          </div>

          <BranchBarChart
            title="Insumos por sucursal"
            rows={d.branches.map((b) => ({ tenantId: b.tenantId, brand: b.brand, label: fmtNumber(b.stockItemCount), value: b.stockItemCount }))}
          />

          <div className="adm-card adm-card--pad0" style={{ marginBottom: 16 }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--pos-separator)' }}>
              <h2 className="adm-card__title" style={{ margin: 0 }}>Desglose por sucursal</h2>
            </div>
            <div className="adm-list">
              {d.branches.length === 0 && (
                <div className="adm-empty">Esta organización aún no tiene sucursales asociadas.</div>
              )}
              {d.branches.map((b) => (
                <div key={b.tenantId} className="adm-row">
                  <div className="adm-row__main">
                    <BranchTitle brand={b.brand} />
                    <div className="adm-row__meta">{b.displayName}</div>
                  </div>
                  <div className="adm-row__tail" style={{ textAlign: 'right', display: 'flex', gap: 16 }}>
                    <Stat label="Insumos" value={fmtNumber(b.stockItemCount)} />
                    <Stat label="Cantidad" value={fmtNumber(b.totalQty, 2)} />
                    <Stat label="Bajo stock" value={fmtNumber(b.lowStockCount)} warn={b.lowStockCount > 0} />
                    <Stat label="Agotados" value={fmtNumber(b.outOfStockCount)} danger={b.outOfStockCount > 0} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="adm-card adm-card--pad0">
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--pos-separator)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={16} style={{ opacity: 0.7 }} />
              <h2 className="adm-card__title" style={{ margin: 0 }}>Alertas de bajo stock (top 20)</h2>
            </div>
            <div className="adm-list">
              {d.topLowStock.length === 0 && (
                <div className="adm-empty">Sin alertas de bajo stock — todos los insumos están sobre el reorder point.</div>
              )}
              {d.topLowStock.map((row) => (
                <div key={row.stockItemId} className="adm-row">
                  <div className="adm-row__main">
                    <div className="adm-row__title">{row.sku ?? row.productId.slice(0, 8)}</div>
                    <div className="adm-row__meta">
                      <code>{row.brand}</code> · reorder {fmtNumber(row.reorderPoint, 2)} {unitShort(row.uom)} · min {fmtNumber(row.minQty, 2)} {unitShort(row.uom)}
                    </div>
                  </div>
                  <div className="adm-row__tail" style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, color: row.qty <= 0 ? 'var(--pos-danger, #dc2626)' : 'var(--pos-warning, #d97706)' }}>
                      {fmtNumber(row.qty, 2)} {unitShort(row.uom)}
                    </div>
                    <div className="adm-row__meta">{row.qty <= 0 ? 'agotado' : 'bajo'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  )
}

function CustomersTab({
  orgId, period, periodKey,
}: {
  orgId: string
  period: { from?: string; to?: string }
  periodKey: string
}): JSX.Element {
  const rollup = useAsync((s) => orgs.getOrgCustomersRollup(orgId, period, s), [orgId, periodKey])
  const d = rollup.data
  return (
    <>
      {asyncState(rollup.loading, rollup.error, rollup.reload)}
      {d && (
        <>
          <div className="adm-grid" style={{ marginBottom: 16 }}>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Clientes activos</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{fmtNumber(d.totals.customerCount)}</div>
              <div className="adm-row__meta">catálogo (status=ACTIVE)</div>
            </div>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Nuevos en período</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{fmtNumber(d.totals.newCount)}</div>
            </div>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Compradores</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{fmtNumber(d.totals.activeBuyerCount)}</div>
              <div className="adm-row__meta">con ≥ 1 orden cerrada</div>
            </div>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Recurrentes</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{fmtNumber(d.totals.recurringCount)}</div>
              <div className="adm-row__meta">con ≥ 2 órdenes</div>
            </div>
          </div>

          <BranchBarChart
            title="Compradores por sucursal"
            rows={d.branches.map((b) => ({ tenantId: b.tenantId, brand: b.brand, label: fmtNumber(b.activeBuyerCount), value: b.activeBuyerCount }))}
          />

          <div className="adm-card adm-card--pad0">
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--pos-separator)' }}>
              <h2 className="adm-card__title" style={{ margin: 0 }}>Desglose por sucursal</h2>
            </div>
            <div className="adm-list">
              {d.branches.length === 0 && (
                <div className="adm-empty">Esta organización aún no tiene sucursales asociadas.</div>
              )}
              {d.branches.map((b) => (
                <div key={b.tenantId} className="adm-row">
                  <div className="adm-row__main">
                    <BranchTitle brand={b.brand} />
                    <div className="adm-row__meta">{b.displayName}</div>
                  </div>
                  <div className="adm-row__tail" style={{ textAlign: 'right', display: 'flex', gap: 16 }}>
                    <Stat label="Clientes" value={fmtNumber(b.customerCount)} />
                    <Stat label="Nuevos" value={fmtNumber(b.newCount)} />
                    <Stat label="Compraron" value={fmtNumber(b.activeBuyerCount)} />
                    <Stat label="Recurrentes" value={fmtNumber(b.recurringCount)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  )
}

function ProductsTab({
  orgId, period, periodKey, limit, setLimit,
}: {
  orgId: string
  period: { from?: string; to?: string }
  periodKey: string
  limit: number
  setLimit: (n: number) => void
}): JSX.Element {
  const rollup = useAsync((s) => orgs.getOrgTopProductsRollup(orgId, period, limit, s), [orgId, periodKey, limit])
  const d = rollup.data
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span className="adm-row__meta">Top:</span>
        {[5, 10, 20, 50].map((n) => (
          <button
            key={n}
            type="button"
            className={`adm-btn adm-btn--sm${limit === n ? ' adm-btn--primary' : ' adm-btn--ghost'}`}
            onClick={() => setLimit(n)}
            aria-pressed={limit === n}
          >{n}</button>
        ))}
      </div>

      {asyncState(rollup.loading, rollup.error, rollup.reload)}
      {d && (
        <div className="adm-card adm-card--pad0">
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--pos-separator)' }}>
            <h2 className="adm-card__title" style={{ margin: 0 }}>Top {d.limit} productos vendidos</h2>
          </div>
          <div className="adm-list">
            {d.products.length === 0 && (
              <div className="adm-empty">No hay ventas registradas en el período seleccionado.</div>
            )}
            {d.products.map((p, idx) => {
              const maxBranch = Math.max(1, ...p.byBranch.map((b) => b.quantity))
              return (
                <div key={p.productId} className="adm-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div>
                      <div className="adm-row__title">
                        <span style={{ display: 'inline-block', width: 24, opacity: 0.5 }}>#{idx + 1}</span>
                        {p.name}
                      </div>
                      <div className="adm-row__meta" style={{ fontSize: 11 }}>productId: {p.productId}</div>
                    </div>
                    <div className="adm-row__tail" style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 600 }}>{fmtNumber(p.totalQuantity)} u.</div>
                      <div className="adm-row__meta">{fmtNumber(p.orderCount)} ord. · {fmtNumber(p.lineCount)} líneas</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 24 }}>
                    {p.byBranch.map((b) => (
                      <div key={b.tenantId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                        <code style={{ minWidth: 160 }}>{b.brand}</code>
                        <div style={{ flex: 1, height: 8, background: 'var(--pos-surface-2, #eee)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ width: `${(b.quantity / maxBranch) * 100}%`, height: '100%', background: 'var(--pos-accent, #4c6ef5)' }} />
                        </div>
                        <span style={{ minWidth: 80, textAlign: 'right' }}>{fmtNumber(b.quantity)} u. · {fmtNumber(b.orderCount)} ord.</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

function KpisTab({
  orgId, period, periodKey,
}: {
  orgId: string
  period: { from?: string; to?: string }
  periodKey: string
}): JSX.Element {
  const rollup = useAsync((s) => orgs.getOrgKpisRollup(orgId, period, s), [orgId, periodKey])
  const d = rollup.data
  return (
    <>
      {asyncState(rollup.loading, rollup.error, rollup.reload)}
      {d && (
        <>
          <div className="adm-grid" style={{ marginBottom: 16 }}>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Ticket promedio</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{mxn(d.totals.avgTicketCents)}</div>
              <div className="adm-row__meta">sum(total) / count</div>
            </div>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Órdenes</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{fmtNumber(d.totals.orderCount)}</div>
            </div>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Hora pico</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{d.totals.peakHour !== null ? `${String(d.totals.peakHour).padStart(2, '0')}:00` : '—'}</div>
              <div className="adm-row__meta">{fmtNumber(d.totals.peakHourOrders)} órdenes</div>
            </div>
            <div className="adm-card adm-card--pad">
              <div className="adm-card__title">Día más fuerte</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{d.totals.peakDow !== null ? DOW_LABELS[d.totals.peakDow] : '—'}</div>
              <div className="adm-row__meta">{fmtNumber(d.totals.peakDowOrders)} órdenes</div>
            </div>
          </div>

          <HeatmapCard heatmap={d.heatmap} />

          <div className="adm-card adm-card--pad0">
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--pos-separator)' }}>
              <h2 className="adm-card__title" style={{ margin: 0 }}>KPIs por sucursal</h2>
            </div>
            <div className="adm-list">
              {d.branches.length === 0 && (
                <div className="adm-empty">Esta organización aún no tiene sucursales asociadas.</div>
              )}
              {d.branches.map((b) => (
                <div key={b.tenantId} className="adm-row">
                  <div className="adm-row__main">
                    <BranchTitle brand={b.brand} />
                    <div className="adm-row__meta">{b.displayName}</div>
                  </div>
                  <div className="adm-row__tail" style={{ textAlign: 'right', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <Stat label="Órdenes" value={fmtNumber(b.orderCount)} />
                    <Stat label="Ticket prom" value={mxn(b.avgTicketCents)} />
                    <Stat label="Hora pico" value={b.peakHour !== null ? `${String(b.peakHour).padStart(2, '0')}:00` : '—'} sub={b.peakHour !== null ? `${fmtNumber(b.peakHourOrders)} ord.` : undefined} />
                    <Stat label="Día pico" value={b.peakDow !== null ? (DOW_LABELS[b.peakDow] ?? '—') : '—'} sub={b.peakDow !== null ? `${fmtNumber(b.peakDowOrders)} ord.` : undefined} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Componentes utilitarios
// ─────────────────────────────────────────────────────────────────────────

function BranchTitle({ brand }: { brand: string }): JSX.Element {
  return (
    <div className="adm-row__title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Building size={14} aria-hidden style={{ opacity: 0.6 }} />
      <code>{brand}</code>
    </div>
  )
}

function Stat({ label, value, sub, warn, danger }: { label: string; value: string; sub?: string; warn?: boolean; danger?: boolean }): JSX.Element {
  const color = danger ? 'var(--pos-danger, #dc2626)' : warn ? 'var(--pos-warning, #d97706)' : undefined
  return (
    <div style={{ minWidth: 80 }}>
      <div style={{ fontWeight: 600, color }}>{value}</div>
      <div className="adm-row__meta" style={{ fontSize: 11 }}>{label}{sub ? ` · ${sub}` : ''}</div>
    </div>
  )
}

/** Bar chart simple horizontal — comparativo entre branches. */
function BranchBarChart({
  title, rows,
}: {
  title: string
  rows: { tenantId: string; brand: string; label: string; value: number }[]
}): JSX.Element | null {
  if (rows.length === 0) return null
  const max = Math.max(1, ...rows.map((r) => r.value))
  return (
    <div className="adm-card adm-card--pad" style={{ marginBottom: 16 }}>
      <div className="adm-card__title" style={{ marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((r) => (
          <div key={r.tenantId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
            <code style={{ minWidth: 160 }}>{r.brand}</code>
            <div style={{ flex: 1, height: 10, background: 'var(--pos-surface-2, #eee)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${(r.value / max) * 100}%`, height: '100%', background: 'var(--pos-accent, #4c6ef5)' }} />
            </div>
            <span style={{ minWidth: 100, textAlign: 'right', fontWeight: 600 }}>{r.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Heatmap 7×24 dow × hour. Color por intensidad relativa al máximo. */
function HeatmapCard({ heatmap }: { heatmap: orgs.OrgKpiHeatCell[] }): JSX.Element {
  const cellMap = new Map<string, number>()
  let max = 0
  for (const c of heatmap) {
    cellMap.set(`${c.dow}-${c.hour}`, c.orderCount)
    if (c.orderCount > max) max = c.orderCount
  }
  return (
    <div className="adm-card adm-card--pad" style={{ marginBottom: 16, overflowX: 'auto' }}>
      <div className="adm-card__title" style={{ marginBottom: 8 }}>Heatmap — día × hora</div>
      <div className="adm-row__meta" style={{ marginBottom: 8 }}>
        {max === 0 ? 'Sin órdenes en el período.' : `Pico: ${max} órdenes en una franja.`}
      </div>
      <table style={{ borderCollapse: 'collapse', fontSize: 10 }}>
        <thead>
          <tr>
            <th style={{ padding: '4px 6px', textAlign: 'right' }}></th>
            {Array.from({ length: 24 }).map((_, h) => (
              <th key={h} style={{ padding: '2px 0', textAlign: 'center', minWidth: 18, color: 'var(--pos-text-2, #888)' }}>
                {h % 3 === 0 ? h : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DOW_LABELS.map((dowLabel, dow) => (
            <tr key={dow}>
              <th style={{ padding: '2px 6px', textAlign: 'right', color: 'var(--pos-text-2, #888)' }}>{dowLabel}</th>
              {Array.from({ length: 24 }).map((_, h) => {
                const v = cellMap.get(`${dow}-${h}`) ?? 0
                const intensity = max === 0 ? 0 : v / max
                const bg = v === 0
                  ? 'transparent'
                  : `rgba(76, 110, 245, ${0.15 + 0.85 * intensity})`
                return (
                  <td
                    key={h}
                    title={`${dowLabel} ${String(h).padStart(2, '0')}:00 — ${v} órd.`}
                    style={{
                      width: 18, height: 18,
                      background: bg,
                      border: '1px solid var(--pos-surface-2, #f0f0f0)',
                    }}
                  />
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
